import React, { useState } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import Button from 'components/styleguide/button/Button';
import Dialog from 'components/styleguide/dialog/Dialog';
import Icon from 'components/styleguide/icon/Icon';
import SelectField from 'components/styleguide/select_field/SelectField';
import Textarea from 'components/styleguide/textarea/Textarea';
import { gateOutcome } from './utils/curationScoring';
import { gateCheckLabel } from './utils/gateCheckPresenter';
import { useCurationReview } from './state/CurationReviewContext';
import { useSubmitDecision } from './state/useSubmitDecision';

const styles = require('./GateDecision.module.scss');

// The single eligibility decision: continue to the AI review (N/A results
// need explicit acceptance) or reject with the automatic, editable reason.
// Confirming it is what unlocks the AI match.
export default function GateDecision() {
  const { dispatch, rejectionReasons, state } = useCurationReview();
  const submit = useSubmitDecision();
  const [confirmingNa, setConfirmingNa] = useState(false);
  const [candidateMessage, setCandidateMessage] = useState('');

  const outcome = gateOutcome(state.gateChecks);
  const reason = state.decision.reason;
  const reasonOption = rejectionReasons.find((option) => option.value === reason) || null;

  const continueToAiReview = () => {
    setConfirmingNa(false);
    dispatch({ type: 'CONFIRM_GATE' });
  };

  const onContinue = () => {
    if (outcome.unresolvedNa.length > 0) {
      setConfirmingNa(true);
    } else {
      continueToAiReview();
    }
  };

  const rejectAndClose = () => {
    if (!reason) return;
    submit({
      decision: 'reject',
      rejection_candidate_message: candidateMessage,
      rejection_reason: reason,
    });
  };

  if (outcome.canContinue) {
    return (
      <div className={styles['ui-gate-decision']}>
        <div className={styles['ui-gate-decision__copy']}>
          <span className={styles['ui-gate-decision__title']}>
            <Icon aria-hidden name="check" size="sm" />
            All checks passed: candidate is eligible
          </span>
          <span className={styles['ui-gate-decision__detail']}>
            Location and language requirements are both met.
          </span>
        </div>
        {!state.gateConfirmed && (
          <Button context="talent" iconRight="arrowRight" onClick={onContinue} size="S" type="primary">
            Continue to AI review
          </Button>
        )}
        <Dialog
          cancelText="Go back"
          confirmText="Accept and continue"
          context="talent"
          isOpen={confirmingNa}
          message={
            <>
              <p>These checks couldn't be verified from the profile:</p>
              <ul>
                {outcome.unresolvedNa.map((check) => (
                  <li key={check.key}>{gateCheckLabel(check)}</li>
                ))}
              </ul>
              <p>Continuing accepts them as reviewed. You can still reject later.</p>
            </>
          }
          onClose={() => setConfirmingNa(false)}
          onConfirm={continueToAiReview}
          title="Some checks are N/A"
        />
      </div>
    );
  }

  if (state.submitted) return null;

  return (
    <div className={`${styles['ui-gate-decision']} ${styles['ui-gate-decision--fail']}`}>
      <div className={styles['ui-gate-decision__copy']}>
        <span className={styles['ui-gate-decision__title']}>
          <Icon aria-hidden name="x" size="sm" />
          An eligibility check failed: the application can only be rejected
        </span>
      </div>
      <div className={styles['ui-gate-decision__form']}>
        <SelectField
          label="Rejection reason"
          onChange={(option: { label: string; value: string } | null) =>
            dispatch({ type: 'SET_DECISION', value: { reason: option ? option.value : null } })
          }
          options={rejectionReasons}
          value={reasonOption}
        />
        <Textarea
          hint="Sent to the candidate with the rejection email. Optional."
          label="Message to the candidate"
          onChange={(event) => setCandidateMessage(event.target.value)}
          rows={3}
        />
        {state.submitError && <Alert message={state.submitError} variant="error" />}
        <Button
          className={styles['ui-gate-decision__reject']}
          context="semantic"
          disabled={!reason || state.submitStatus === 'submitting'}
          iconLeft="x"
          onClick={rejectAndClose}
          size="S"
          spinner={state.submitStatus === 'submitting'}
          type="primary"
        >
          Reject &amp; close
        </Button>
      </div>
    </div>
  );
}
